<script setup lang="ts">
import { useSlots, computed } from 'vue';

const slots = useSlots();
const props = defineProps({
	type: {
		type: String,
		default: 'radio'
	},
	name: {
		type: String,
		default: null
	},
	dark: {
		type: Boolean,
		default: false
	},
	value: {
		type: [String, Number],
		default: null
	},
});
const modelValue = defineModel();
const modes = computed(() => ({
	'form-radio-input_dark': props.dark,
}));
</script>
<template>
	<label :class="['form-radio-input', modes]">
		<input
			:type="type"
			:name="name"
			:value="value"
			v-model="modelValue"
			class="form-radio-input__input"
		>
		<span class="form-radio-input__content">
			<span class="form-radio-input__icon"></span>
			<span class="form-radio-input__title">
				<slot />
			</span>
			<span
				v-if="slots.append"
				class="form-radio-input__append"
			>
				<slot name="append" />
			</span>
		</span>
	</label>
</template>
<style lang="scss">
.form-radio-input {
	cursor: pointer;
	display: block;
	&_dark {
		.form-radio-input {
			&__content {
				border-color: #DDDCDB;
				background-color: transparent;
				transition: ease 0.2s;
			}
			&__icon {
				border-color: #DDDCDB;
				&::before {
					background-color: #BB45C1;
				}
			}
			&__input {
				display: none;
				&:checked {
					&+.form-radio-input {
						&__content {
							border-color: #BB45C1;
							.form-radio-input {
								&__icon {
									border-color: #DDDCDB;
								}
							}
						}
					}
				}
			}
		}
	}

	&__input {
		display: none;
		&:checked {
			&+.form-radio-input {
				&__content {
					border-color: transparent;
					background-color: var(--color-purple)!important;
					color: var(--color-white);
					.form-radio-input {
						&__append {
							color: inherit;
						}
						&__icon {
							border-color: currentColor;
							&::before {
								opacity: 1;
								visibility: visible;
								transform: scale(1);
							}
						}
					}
				}
			}
		}
	}

	&__title {
		opacity: 0.7;
		flex: 1 1 auto;
		min-width: 0;
	}

	&__icon {
		width: 2rem;
		height: 2rem;
		background: rgba(var(--color-active-rgb), 0.12);
		border: 1px solid var(--color-purple);
		border-radius: 50%;
		flex: 0 0 auto;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		&::before {
			content: '';
			display: block;
			width: 1.2rem;
			height: 1.2rem;
			border-radius: 50%;
			background-color: var(--color-white);
			opacity: 0;
			visibility: hidden;
			transform: scale(0.7);
			transition: ease 0.2s;
		}
	}

	&__append {
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		gap: 1.2rem;
		color: var(--color-purple);
	}

	&__content {
		display: flex;
		align-items: center;
		height: 5.2rem;
		gap: 1.6rem;
		padding: 0 1.6rem;
		border-radius: 1.6rem;
		transition: ease 0.13s;
		background-color: var(--color-panel);
		border: 1px solid var(--color-panel);
		color: var(--color-text);
		transition: color 0.17s, background-color 0.17s, border-color 0.17s;
		@include min-desktop {
			&:hover {
				background-color: rgba(var(--color-active-rgb), 0.12);
			}
		}
		@include theme-dark {
			border-color: rgba(var(--color-active-rgb), 0.04);
			background-color: rgba(var(--color-active-rgb), 0.04);
			@include min-desktop {
				&:hover {
					background-color: rgba(var(--color-active-rgb), 0.06);
				}
			}
		}
	}
}
</style>
